import React, { useState, useEffect } from 'react';
import {
  Award, TrendingUp, TrendingDown, AlertTriangle, CheckCircle,
  Lightbulb, RefreshCw, Target, Zap, ChevronRight
} from 'lucide-react';
import { getGoogleAdsClient, getDateRange } from '@/lib/google-ads-client';
import { createGoogleAdsAIOptimizer, type QualityScoreImprovement } from '@/lib/google-ads-ai-optimizer';
import { supabase } from '@/lib/supabase';
import toast from 'react-hot-toast';
import { useGoogleAdsCompany, GOOGLE_ADS_COMPANY_OPTIONS } from '@/hooks/useGoogleAdsCompany';

interface QualityScoreKeyword {
  keywordId: string;
  keywordText: string;
  qualityScore: number;
  creativeScore: string;
  landingPageScore: string;
  ctrScore: string;
  trend: 'up' | 'down' | 'stable';
  improvements?: QualityScoreImprovement;
}

export default function QualityScoreMonitorPage() {
  const [keywords, setKeywords] = useState<QualityScoreKeyword[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedKeyword, setSelectedKeyword] = useState<QualityScoreKeyword | null>(null);
  const [filterScore, setFilterScore] = useState<string>('all');
  const { company, setCompany } = useGoogleAdsCompany();

  useEffect(() => {
    if (!company) return;
    loadQualityScores();
  }, [company]);

  const loadQualityScores = async () => {
    try {
      setLoading(true);

      const { data: mappings } = await supabase
        .from('google_ads_customer_mapping')
        .select('*')
        .eq('company', company)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();

      if (!mappings) {
        toast.error('No Google Ads account connected');
        return;
      }

      const customerId = mappings.customer_id;
      const client = getGoogleAdsClient(customerId);
      const range = getDateRange(30);

      // Get quality scores and performance data
      const [qualityScores, keywordMetrics] = await Promise.all([
        client.getQualityScores(customerId, {}),
        client.getKeywordMetrics(customerId, { dateRange: range }),
      ]);

      // Get AI improvement recommendations
      const optimizer = createGoogleAdsAIOptimizer(client);
      const report = await optimizer.generateOptimizationReport(customerId, range);

      // Combine data
      const keywordsWithScores: QualityScoreKeyword[] = qualityScores.map(qs => {
        const metrics = keywordMetrics.find(km => km.keywordId === qs.keywordId);
        const improvement = report.qualityScoreImprovements.find(
          imp => imp.keywordId === qs.keywordId
        );

        return {
          keywordId: qs.keywordId,
          keywordText: qs.keywordText,
          qualityScore: qs.qualityScore,
          creativeScore: qs.creativeQualityScore,
          landingPageScore: qs.landingPageExperienceQualityScore,
          ctrScore: qs.searchPredictedCtr,
          trend: 'stable', // Would calculate from historical data
          improvements: improvement,
        };
      });

      // Sort by quality score (lowest first)
      keywordsWithScores.sort((a, b) => a.qualityScore - b.qualityScore);

      setKeywords(keywordsWithScores);

    } catch (error) {
      console.error('Error loading quality scores:', error);
      toast.error('Failed to load quality scores');
    } finally {
      setLoading(false);
    }
  };

  const getFilteredKeywords = () => {
    if (filterScore === 'all') return keywords;
    if (filterScore === 'poor') return keywords.filter(k => k.qualityScore < 5);
    if (filterScore === 'average') return keywords.filter(k => k.qualityScore >= 5 && k.qualityScore < 7);
    if (filterScore === 'good') return keywords.filter(k => k.qualityScore >= 7);
    return keywords;
  };

  const filteredKeywords = getFilteredKeywords();

  const avgQualityScore = keywords.length > 0
    ? keywords.reduce((sum, k) => sum + k.qualityScore, 0) / keywords.length
    : 0;

  const poorKeywords = keywords.filter(k => k.qualityScore < 5).length;
  const averageKeywords = keywords.filter(k => k.qualityScore >= 5 && k.qualityScore < 7).length;
  const goodKeywords = keywords.filter(k => k.qualityScore >= 7).length;

  const getScoreBadgeColor = (score: number) => {
    if (score >= 8) return 'bg-green-100 text-green-800 border-green-300';
    if (score >= 6) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-red-100 text-red-800 border-red-300';
  };

  const getComponentBadgeColor = (component: string) => {
    if (component === 'ABOVE_AVERAGE') return 'bg-green-100 text-green-700';
    if (component === 'AVERAGE') return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
          <span className="ml-3 text-lg">Loading quality scores...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Quality Score Monitor</h1>
        <p className="text-gray-600">Track and improve keyword quality scores to reduce costs</p>
      </div>

      <div className="flex justify-end">
        <select
          value={company}
          onChange={(e) => setCompany(e.target.value as 'hales' | 'myccs' | 'abraham')}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          {GOOGLE_ADS_COMPANY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm">Average Score</span>
            <Award className="h-5 w-5 text-blue-500" />
          </div>
          <div className="text-3xl font-bold text-gray-900">
            {avgQualityScore.toFixed(1)}<span className="text-lg text-gray-500">/10</span>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm">Poor (&lt;5)</span>
            <AlertTriangle className="h-5 w-5 text-red-500" />
          </div>
          <div className="text-3xl font-bold text-red-600">{poorKeywords}</div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm">Average (5-6)</span>
            <Target className="h-5 w-5 text-yellow-500" />
          </div>
          <div className="text-3xl font-bold text-yellow-600">{averageKeywords}</div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm">Good (7+)</span>
            <CheckCircle className="h-5 w-5 text-green-500" />
          </div>
          <div className="text-3xl font-bold text-green-600">{goodKeywords}</div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-lg shadow p-4 mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <select
            value={filterScore}
            onChange={(e) => setFilterScore(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">All Keywords ({keywords.length})</option>
            <option value="poor">Poor Scores (&lt;5) - {poorKeywords}</option>
            <option value="average">Average Scores (5-6) - {averageKeywords}</option>
            <option value="good">Good Scores (7+) - {goodKeywords}</option>
          </select>
        </div>

        <button
          onClick={loadQualityScores}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Keywords List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Keywords ({filteredKeywords.length})
          </h2>
        </div>

        <div className="divide-y divide-gray-200">
          {filteredKeywords.map((keyword) => (
            <div key={keyword.keywordId} className="p-6 hover:bg-gray-50 transition-colors">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{keyword.keywordText}</h3>
                    <span className={`px-3 py-1 rounded-full text-sm font-bold border-2 ${getScoreBadgeColor(keyword.qualityScore)}`}>
                      {keyword.qualityScore}/10
                    </span>
                  </div>

                  {/* Component Scores */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Ad Relevance:</span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getComponentBadgeColor(keyword.creativeScore)}`}>
                        {keyword.creativeScore.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Landing Page:</span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getComponentBadgeColor(keyword.landingPageScore)}`}>
                        {keyword.landingPageScore.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Expected CTR:</span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getComponentBadgeColor(keyword.ctrScore)}`}>
                        {keyword.ctrScore.replace('_', ' ')}
                      </span>
                    </div>
                  </div>

                  {/* AI Improvements */}
                  {keyword.improvements && (
                    <button
                      onClick={() => setSelectedKeyword(keyword)}
                      className="flex items-center gap-2 text-purple-600 hover:text-purple-700 text-sm font-medium"
                    >
                      <Lightbulb className="h-4 w-4" />
                      View {Object.keys(keyword.improvements.improvements).length} AI Recommendations
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {/* Score Visual */}
                <div className="flex flex-col items-center">
                  <div className="relative w-20 h-20">
                    <svg className="w-20 h-20 transform -rotate-90">
                      <circle
                        cx="40"
                        cy="40"
                        r="32"
                        stroke="currentColor"
                        strokeWidth="8"
                        fill="none"
                        className="text-gray-200"
                      />
                      <circle
                        cx="40"
                        cy="40"
                        r="32"
                        stroke="currentColor"
                        strokeWidth="8"
                        fill="none"
                        strokeDasharray={`${(keyword.qualityScore / 10) * 201} 201`}
                        className={
                          keyword.qualityScore >= 8
                            ? 'text-green-500'
                            : keyword.qualityScore >= 6
                            ? 'text-yellow-500'
                            : 'text-red-500'
                        }
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-xl font-bold text-gray-900">{keyword.qualityScore}</span>
                    </div>
                  </div>
                  {keyword.improvements && (
                    <div className="mt-2 text-xs text-gray-500">
                      Target: {keyword.improvements.targetScore}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredKeywords.length === 0 && (
          <div className="p-12 text-center text-gray-500">
            <Award className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p>No keywords match current filter</p>
          </div>
        )}
      </div>

      {/* Improvement Modal */}
      {selectedKeyword && selectedKeyword.improvements && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{selectedKeyword.keywordText}</h3>
                  <p className="text-sm text-gray-600 mt-1">Quality Score Improvement Plan</p>
                </div>
                <button
                  onClick={() => setSelectedKeyword(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Score Progress */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Current Score</div>
                    <div className="text-3xl font-bold text-gray-900">{selectedKeyword.improvements.currentScore}</div>
                  </div>
                  <ChevronRight className="h-8 w-8 text-gray-400" />
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Target Score</div>
                    <div className="text-3xl font-bold text-green-600">{selectedKeyword.improvements.targetScore}</div>
                  </div>
                </div>
                <div className="mt-4 text-sm text-gray-700">
                  {selectedKeyword.improvements.estimatedImpact}
                </div>
              </div>

              {/* Improvement Sections */}
              <div className="space-y-6">
                {selectedKeyword.improvements.improvements.adRelevance && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Zap className="h-5 w-5 text-purple-600" />
                      Ad Relevance Improvements
                    </h4>
                    <ul className="space-y-2">
                      {selectedKeyword.improvements.improvements.adRelevance.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
                          <CheckCircle className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {selectedKeyword.improvements.improvements.landingPageExperience && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Target className="h-5 w-5 text-blue-600" />
                      Landing Page Improvements
                    </h4>
                    <ul className="space-y-2">
                      {selectedKeyword.improvements.improvements.landingPageExperience.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                          <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {selectedKeyword.improvements.improvements.expectedCTR && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-green-600" />
                      CTR Improvements
                    </h4>
                    <ul className="space-y-2">
                      {selectedKeyword.improvements.improvements.expectedCTR.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setSelectedKeyword(null)}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quality Score Guide */}
      <div className="mt-8 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 border border-blue-200">
        <h3 className="font-semibold text-gray-900 mb-4">Understanding Quality Score</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="font-medium text-gray-900 mb-2">Why It Matters</div>
            <p className="text-gray-700">Higher quality scores lead to lower CPCs and better ad positions. Each point improvement can reduce costs by ~5-10%.</p>
          </div>
          <div>
            <div className="font-medium text-gray-900 mb-2">Score Ranges</div>
            <ul className="space-y-1 text-gray-700">
              <li>• 8-10: Excellent (maximize these)</li>
              <li>• 6-7: Good (minor improvements)</li>
              <li>• 1-5: Poor (immediate action needed)</li>
            </ul>
          </div>
          <div>
            <div className="font-medium text-gray-900 mb-2">Components</div>
            <ul className="space-y-1 text-gray-700">
              <li>• Expected CTR (most important)</li>
              <li>• Ad Relevance (keyword-ad match)</li>
              <li>• Landing Page Experience (UX)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
